import type { CustomerWithStats } from '@/types';
import { theme } from '@/lib/theme';

interface Props {
  customers: CustomerWithStats[];
  loading: boolean;
  onSelect: (id: string) => void;
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  const [year, month, day] = dateStr.split('-');
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${day} ${months[parseInt(month, 10) - 1]} ${year}`;
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(amount);
}

export default function CustomersTable({ customers, loading, onSelect }: Props) {
  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{ backgroundColor: '#141414', borderColor: theme.colors.border }}
    >
      {/* ── MOBILE CARD VIEW ─────────────────────────────────── */}
      <div className="md:hidden divide-y" style={{ borderColor: theme.colors.border }}>
        {loading && (
          [...Array(5)].map((_, i) => (
            <div key={i} className="p-4 flex items-center gap-3 animate-pulse">
              <div className="w-10 h-10 rounded-full bg-white/8 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3.5 bg-white/8 rounded w-32" />
                <div className="h-3 bg-white/6 rounded w-44" />
              </div>
              <div className="h-5 bg-white/8 rounded-full w-8" />
            </div>
          ))
        )}

        {!loading && customers.length === 0 && (
          <div className="px-5 py-14 text-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5">
                <i className="ri-user-line text-white/25 text-xl" />
              </div>
              <p className="text-white/30 text-sm">No se encontraron clientes</p>
              <p className="text-white/20 text-xs">Los clientes aparecerán aquí cuando realicen su primera reserva</p>
            </div>
          </div>
        )}

        {!loading && customers.map((customer) => (
          <div
            key={customer.id}
            onClick={() => onSelect(customer.id)}
            className="p-4 flex items-center gap-3 cursor-pointer active:bg-white/5 transition-colors"
          >
            {/* Avatar */}
            <div
              className="w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 text-sm font-semibold"
              style={{ backgroundColor: theme.colors.primaryMuted, color: theme.colors.primary }}
            >
              {customer.name.charAt(0).toUpperCase()}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{customer.name}</p>
              <p className="text-white/40 text-xs truncate">{customer.email}</p>
              <div className="flex items-center gap-3 mt-1">
                {customer.total_spent > 0 && (
                  <span className="text-white/60 text-xs font-mono">{formatCurrency(customer.total_spent)}</span>
                )}
                {customer.last_visit && (
                  <span className="text-white/30 text-xs">{formatDate(customer.last_visit)}</span>
                )}
              </div>
            </div>

            {/* Appointment count + chevron */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <span
                className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold"
                style={{ backgroundColor: theme.colors.primaryMuted, color: theme.colors.primary }}
              >
                {customer.total_appointments}
              </span>
              <i className="ri-arrow-right-s-line text-white/20 text-base" />
            </div>
          </div>
        ))}
      </div>

      {/* ── DESKTOP TABLE VIEW ───────────────────────────────── */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b" style={{ borderColor: theme.colors.border }}>
              <th className="text-left text-white/35 text-xs font-medium uppercase tracking-wider px-5 py-3.5">Cliente</th>
              <th className="text-left text-white/35 text-xs font-medium uppercase tracking-wider px-5 py-3.5 hidden md:table-cell">Email</th>
              <th className="text-left text-white/35 text-xs font-medium uppercase tracking-wider px-5 py-3.5 hidden lg:table-cell">Teléfono</th>
              <th className="text-center text-white/35 text-xs font-medium uppercase tracking-wider px-5 py-3.5">Citas</th>
              <th className="text-right text-white/35 text-xs font-medium uppercase tracking-wider px-5 py-3.5 hidden sm:table-cell">Total gastado</th>
              <th className="text-right text-white/35 text-xs font-medium uppercase tracking-wider px-5 py-3.5 hidden xl:table-cell">Última visita</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <>
                {[...Array(8)].map((_, i) => (
                  <tr key={i} className="border-b border-white/5 animate-pulse">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-white/8 flex-shrink-0" />
                        <div className="h-3.5 bg-white/8 rounded w-28" />
                      </div>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell"><div className="h-3 bg-white/6 rounded w-40" /></td>
                    <td className="px-5 py-4 hidden lg:table-cell"><div className="h-3 bg-white/6 rounded w-24" /></td>
                    <td className="px-5 py-4 text-center"><div className="h-5 bg-white/8 rounded-full w-8 mx-auto" /></td>
                    <td className="px-5 py-4 hidden sm:table-cell text-right"><div className="h-3 bg-white/6 rounded w-20 ml-auto" /></td>
                    <td className="px-5 py-4 hidden xl:table-cell text-right"><div className="h-3 bg-white/6 rounded w-16 ml-auto" /></td>
                  </tr>
                ))}
              </>
            )}

            {!loading && customers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/5">
                      <i className="ri-user-line text-white/25 text-xl" />
                    </div>
                    <p className="text-white/30 text-sm">No se encontraron clientes</p>
                    <p className="text-white/20 text-xs">Los clientes aparecerán aquí cuando realicen su primera reserva</p>
                  </div>
                </td>
              </tr>
            )}

            {!loading && customers.map((customer, idx) => (
              <tr
                key={customer.id}
                onClick={() => onSelect(customer.id)}
                className={`border-b border-white/5 hover:bg-white/3 transition-colors cursor-pointer ${idx === customers.length - 1 ? 'border-b-0' : ''}`}
              >
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 text-sm font-semibold"
                      style={{ backgroundColor: theme.colors.primaryMuted, color: theme.colors.primary }}
                    >
                      {customer.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-white text-sm font-medium">{customer.name}</span>
                  </div>
                </td>
                <td className="px-5 py-4 hidden md:table-cell">
                  <span className="text-white/45 text-sm">{customer.email}</span>
                </td>
                <td className="px-5 py-4 hidden lg:table-cell">
                  <span className="text-white/50 text-sm">{customer.phone ?? '—'}</span>
                </td>
                <td className="px-5 py-4 text-center">
                  <span
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: theme.colors.primaryMuted, color: theme.colors.primary }}
                  >
                    {customer.total_appointments}
                  </span>
                </td>
                <td className="px-5 py-4 hidden sm:table-cell text-right">
                  <span className="text-white/70 text-sm font-mono">
                    {customer.total_spent > 0 ? formatCurrency(customer.total_spent) : '—'}
                  </span>
                </td>
                <td className="px-5 py-4 hidden xl:table-cell text-right">
                  <span className="text-white/50 text-sm">{formatDate(customer.last_visit)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>{/* end hidden md:block */}
    </div>
  );
}
